<template>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div
        class="group my-4 rounded-lg border-iron-200 bg-white px-4 py-5 shadow-sm hover:shadow-md w-full border transition-all duration-200"
    >
        <div class="space-x-3 sm:flex items-center justify-between">
            <div class="gap-2 flex items-center">
                <div class="flex-shrink-0"></div>
                <div class="min-w-0 flex-1">
                    <p class="font-medium text-iron-900">
                        {{ workflow.name }}
                        <span
                            v-if="workflow.default"
                            class="ml-1 rounded-md bg-iron-50 px-2 py-1 text-xs font-medium text-iron-600 ring-iron-500/10 inline-flex items-center ring-1 ring-inset"
                            >{{ $t("workflows.default") }}</span
                        >

                        <span
                            v-if="integrationLabel"
                            class="ml-1 rounded-md bg-z-primary-600 px-2 py-1 text-xs font-medium text-white ring-z-primary-600 inline-flex items-center ring-1 ring-inset"
                        >
                            {{ integrationLabel }}
                        </span>
                    </p>
                    <p
                        v-if="workflow.trigger !== 'reservation_will_happen_in'"
                        class="text-sm text-iron-700"
                    >
                        {{
                            $t(
                                "workflows.trigger_send_to_" +
                                    workflow.target +
                                    "_when_" +
                                    workflow.trigger,
                            )
                        }}
                        <slot name="action-extra" :workflow="workflow" />
                    </p>

                    <p v-else class="text-sm text-iron-700">
                        {{
                            $t(
                                "workflows.trigger_send_to_" +
                                    workflow.target +
                                    "_event_happen_in",
                            )
                        }}
                        {{
                            convertSeconds(
                                JSON.parse(workflow.data as any).timeBefore,
                            )
                        }}.
                    </p>

                    <div
                        v-if="assignedEventTypes.length"
                        class="mt-3 text-sm text-iron-700"
                    >
                        <span
                            v-for="et in visibleAssignedEventTypes"
                            :key="et.id"
                            class="mr-2 gap-1 border-iron-200 bg-white px-2 py-0.5 text-xs font-medium text-iron-800 inline-flex items-center rounded-full border"
                        >
                            <span>{{ et.name }}</span>
                        </span>

                        <AssignEventTypeModal
                            v-if="remainingAssignedCount > 0"
                            :event-types="eventTypes"
                            :personal-event-types="personalEventTypes"
                            :team-event-types="teamEventTypes"
                            :workflow-id="workflow.id"
                            :can-assign-team-workflows="canAssignTeamWorkflows"
                        >
                            <button
                                type="button"
                                class="text-xs font-medium text-iron-700 align-middle underline decoration-dotted"
                            >
                                {{
                                    $t(
                                        "workflows.and_more",
                                        remainingAssignedCount,
                                    )
                                }}
                            </button>
                        </AssignEventTypeModal>
                    </div>

                    <div class="mt-3" v-if="assignedEventTypes.length === 0">
                        <button
                            type="button"
                            class="gap-1 rounded-md bg-z-primary-100 px-2 py-1 text-xs font-medium text-z-primary-600 ring-z-primary-600 relative inline-flex items-center ring-1 ring-inset"
                            @click="assignToAllEventTypes"
                        >
                            <CalendarPlus :stroke-width="1.6" :size="20" />
                            <span class="sm:inline-block hidden">
                                {{ $t("workflows.assign_all") }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <div
                class="mt-3 sm:mt-0 flex flex-shrink-0 justify-start self-center"
            >
                <div class="rounded-md shadow-sm inline-flex">
                    <!-- One-click assign all button (shown only when no event types assigned) -->

                    <AssignEventTypeModal
                        :event-types="eventTypes"
                        :personal-event-types="personalEventTypes"
                        :team-event-types="teamEventTypes"
                        :workflow-id="workflow.id"
                        :can-assign-team-workflows="canAssignTeamWorkflows"
                    >
                        <button
                            type="button"
                            :class="{
                                'gap-1 bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-iron-300 hover:bg-iron-50 relative inline-flex items-center ring-1 ring-inset focus:z-10': true,
                                'rounded-l-md': assignedEventTypes.length > 0,
                                '-ml-px rounded-none':
                                    assignedEventTypes.length === 0,
                            }"
                        >
                            <CalendarPlus :stroke-width="1.6" :size="20" />
                            <span class="sm:inline-block hidden w-[50px]">
                                {{ $t("workflows.assign") }}
                            </span>
                        </button>
                    </AssignEventTypeModal>

                    <!-- Edit Template Button (only for email workflows) -->
                    <div v-if="isEmailAction" class="relative">
                        <!-- Pro Lock Icon -->
                        <div
                            v-if="!hasSubscription"
                            class="-right-1 -top-1 h-5 w-5 bg-white shadow-md absolute z-50 flex items-center justify-center rounded-full transition-all duration-150 hover:scale-110"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.33319 13.3332H12.6665M1.33319 2.6665L3.33319 10.6665H12.6665L14.6665 2.6665L10.6665 7.33317L7.99986 2.6665L5.33319 7.33317L1.33319 2.6665Z"
                                    stroke="url(#paint0_linear_1067_2699)"
                                    stroke-width="1.4"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <defs>
                                    <linearGradient
                                        id="paint0_linear_1067_2699"
                                        x1="1.00018"
                                        y1="1.99976"
                                        x2="10.1364"
                                        y2="14.5624"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop stop-color="#FF8D47" />
                                        <stop
                                            offset="0.329612"
                                            stop-color="#FF8947"
                                        />
                                        <stop
                                            offset="0.534043"
                                            stop-color="#FF4247"
                                        />
                                        <stop
                                            offset="0.68692"
                                            stop-color="#E92A5B"
                                        />
                                        <stop
                                            offset="0.828171"
                                            stop-color="#DD1C67"
                                        />
                                        <stop offset="1" stop-color="#CF0283" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <button
                            type="button"
                            class="gap-1 bg-white px-3 py-2 text-sm font-medium ring-iron-300 relative -ml-px inline-flex items-center ring-1 ring-inset focus:z-10"
                            :class="{
                                'text-iron-900 hover:bg-iron-50':
                                    hasSubscription,
                                'text-iron-500 cursor-pointer':
                                    !hasSubscription,
                            }"
                            @click="
                                !hasSubscription
                                    ? openSubscriptionModal()
                                    : editTemplate()
                            "
                        >
                            <Edit :stroke-width="1.6" :size="20" />
                            <span class="sm:inline-block hidden">{{
                                $t("workflows.edit_template")
                            }}</span>
                        </button>
                    </div>

                    <button
                        :disabled="workflow.default"
                        class="gap-1 rounded-r-md px-3 py-2 text-sm font-medium ring-iron-300 relative -ml-px inline-flex items-center ring-1 ring-inset focus:z-10"
                        :class="{
                            'bg-iron-100 text-iron-400 cursor-not-allowed':
                                workflow.default,
                            'bg-white text-iron-900 hover:bg-iron-50':
                                !workflow.default,
                        }"
                        @click="removeWorkflow"
                    >
                        <Trash2 :stroke-width="1.6" :size="20" />
                        <span class="sm:inline-block hidden">{{
                            $t("workflows.remove")
                        }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { onClickOutside } from "@vueuse/core";
import { Link, router } from "@inertiajs/vue3";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";

import { Trash2, CalendarPlus, Edit } from "lucide-vue-next";
import AssignEventTypeModal from "./AssignEventTypeModal.vue";
import { useI18n } from "vue-i18n";
import route from "ziggy-js";
import { useToast } from "@/components/ui/toast/use-toast";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";

export type Workflow = {
    id: number;
    team_id: number;
    name: string;
    default: boolean;
    trigger: string;
    target: "host" | "attendees";
    attach_ics: boolean;
    data: string | null;
    action: string;
    integration_id?: number | null;
    created_at: string;
    updated_at: string;
};

const props = defineProps<{
    workflow: Workflow;
    eventTypes: Array<any>;
    personalEventTypes?: Array<any>;
    teamEventTypes?: Array<any>;
    canAssignTeamWorkflows?: boolean;
}>();

const { t } = useI18n();
const { toast } = useToast();
const { hasSubscription } = useHasSubscription();
const proDialog = useProDialog();

const target = ref(null);
const opened = ref(false);
const isAssigningAll = ref(false);

const { show } = useConfirm();

onClickOutside(target, () => (opened.value = false));

const removeWorkflow = async () => {
    if (props.workflow.default) {
        return;
    }

    const result = await show({
        header: t("workflows.remove_workflow_modal.are_you_sure"),
        message: t("workflows.remove_workflow_modal.delete"),
        confirm: t("remove"),
        cancel: t("cancel"),
    });

    if (result) {
        await router.delete(`workflows/${props.workflow.id}`);
    }
};

const assignToAllEventTypes = async () => {
    if (isAssigningAll.value) return;

    isAssigningAll.value = true;

    try {
        await router.post(
            `/workflows/${props.workflow.id}/event-types/assign-all`,
            {},
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    toast({
                        title: t("workflows.assign_all_success_title"),
                        description: t(
                            "workflows.assign_all_success_description",
                        ),
                    });
                },
                onError: (errors) => {
                    console.error(
                        "Error assigning workflow to all event types:",
                        errors,
                    );
                    toast({
                        title: t("workflows.assign_all_error_title"),
                        description: t(
                            "workflows.assign_all_error_description",
                        ),
                        variant: "destructive",
                    });
                },
                onFinish: () => {
                    isAssigningAll.value = false;
                },
            },
        );
    } catch (error) {
        console.error("Error assigning workflow to all event types:", error);
        toast({
            title: t("workflows.assign_all_error_title"),
            description: t("workflows.assign_all_error_description"),
            variant: "destructive",
        });
        isAssigningAll.value = false;
    }
};

const convertSeconds = function (seconds: number) {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    const result = [];

    if (days > 0) {
        result.push(`${days} ${t("workflows.days", days)}`);
    }

    const remainingHours = hours % 24;
    if (remainingHours > 0) {
        result.push(
            `${remainingHours} ${t("workflows.hours", remainingHours)}`,
        );
    }

    const remainingMinutes = minutes % 60;
    if (remainingMinutes > 0) {
        result.push(
            `${remainingMinutes} ${t("workflows.minutes", remainingMinutes)}`,
        );
    }

    const remainingSeconds = seconds % 60;
    if (remainingSeconds > 0) {
        result.push(
            `${remainingSeconds} ${t("workflows.seconds", remainingSeconds)}`,
        );
    }

    return result.join(", ");
};

const isEmailAction = computed(() => props.workflow.action === "send_email");

const actionLabel = computed(() => {
    const key = `workflows.actions.${props.workflow.action}`;
    const translated = t(key);
    return translated === key ? props.workflow.action : translated;
});

const triggerLabel = computed(() => {
    const key = `workflows.triggers.${props.workflow.trigger}`;
    const translated = t(key);
    return translated === key ? props.workflow.trigger : translated;
});

const integrationType = computed(() => {
    switch (props.workflow.action) {
        case "send_email":
            return "EMAIL";
        case "add_task_to_freelo":
            return "FREELO";
        case "add_contact_to_ecomail":
            return "ECOMAIL";
        case "add_person_to_pipedrive":
            return "PIPEDRIVE";
        case "add_lead_to_raynet":
            return "RAYNET";
        case "add_contact_to_hubspot":
            return "HUBSPOT";
        case "send_webhook":
            return "WEBHOOK";
        default:
            return null;
    }
});

const integrationLabel = computed(() => {
    const type = integrationType.value;
    if (!type) return null;
    const map: Record<string, string> = {
        EMAIL: "Email",
        ECOMAIL: "Ecomail",
        FREELO: "Freelo",
        HUBSPOT: "HubSpot",
        PIPEDRIVE: "Pipedrive",
        RAYNET: "Raynet",
        WEBHOOK: "Webhook",
    };
    return map[type] ?? type;
});

const workflowDescription = computed(() => {
    if (props.workflow.trigger === "reservation_will_happen_in") {
        let time = "";
        try {
            const parsed = JSON.parse(props.workflow.data as any);
            if (parsed?.timeBefore) {
                time = convertSeconds(parsed.timeBefore);
            }
        } catch (e) {
            // ignore
        }

        if (isEmailAction.value) {
            return (
                t(
                    `workflows.trigger_send_to_${props.workflow.target}_event_happen_in`,
                ) + ` ${time}.`
            );
        }

        return `${actionLabel.value} — ${t(
            "workflows.triggers.reservation_will_happen_in",
        )} ${time}.`;
    }

    if (isEmailAction.value) {
        return t(
            `workflows.trigger_send_to_${props.workflow.target}_when_${props.workflow.trigger}`,
        );
    }

    return `${actionLabel.value} — ${triggerLabel.value}`;
});

const assignedEventTypes = computed(() => {
    const list = (props.eventTypes || []).filter((et: any) => {
        const wfs = et?.workflows || [];
        return wfs.some((w: any) => w?.id === props.workflow.id);
    });
    return list;
});

const VISIBLE_ASSIGNED_LIMIT = 5;
const visibleAssignedEventTypes = computed(() =>
    assignedEventTypes.value.slice(0, VISIBLE_ASSIGNED_LIMIT),
);
const remainingAssignedCount = computed(() =>
    Math.max(0, assignedEventTypes.value.length - VISIBLE_ASSIGNED_LIMIT),
);

function editTemplate() {
    router.visit(route("app.workflows.show", { workflow: props.workflow.id }));
}

function openSubscriptionModal() {
    proDialog.show({
        feature: "customize_confirmation_email",
    });
}
</script>

<style>
@import url("/node_modules/@zaptime/vue3/dist/style.css");
</style>
